<template>
  <dashboard-layout title="Email Thread" back-to="/email-replies" back-label="Email Replies">
    <template #actions>
      <template v-if="thread">
        <status-badge :variant="thread.userFound ? 'success' : 'neutral'">
          {{ thread.userFound ? 'User Found' : 'No User Match' }}
        </status-badge>
        <router-link v-if="thread.userId" :to="`/user/${thread.userId}`" class="user-link">View User →</router-link>
      </template>
    </template>

    <loading-state v-if="loading" label="Loading thread…" />

    <empty-state v-else-if="!thread" title="Thread not found" icon="mail">
      This email thread could not be loaded.
      <router-link to="/email-replies" class="user-link">Back to Email Replies →</router-link>
    </empty-state>

    <template v-else>
      <data-panel>
        <template #title>
          <span class="thread-subject">{{ thread.subject || '(no subject)' }}</span>
          <status-badge :variant="threadStatusVariant">{{ threadStatusLabel }}</status-badge>
        </template>

        <div class="meta-grid">
          <div class="meta-item">
            <span class="meta-label">Customer</span>
            <span class="meta-value">
              <span v-if="thread.customerName">{{ thread.customerName }} &lt;{{ thread.customerEmail }}&gt;</span>
              <span v-else>{{ thread.customerEmail }}</span>
            </span>
          </div>
          <div class="meta-item">
            <span class="meta-label">Messages</span>
            <span class="meta-value">{{ thread.messageCount || 0 }}</span>
          </div>
          <div class="meta-item">
            <span class="meta-label">Started</span>
            <span class="meta-value">{{ formatTimestamp(thread.createdAt) }}</span>
          </div>
          <div class="meta-item">
            <span class="meta-label">Last Activity</span>
            <span class="meta-value">{{ formatTimestamp(thread.lastActivityAt) }}</span>
          </div>
        </div>
      </data-panel>

      <data-panel title="Conversation">
        <template #actions>
          <span class="msg-count" v-if="messages.length > 0">{{ messages.length }} message{{ messages.length === 1 ? '' : 's' }}</span>
        </template>

        <div class="conversation">
          <div
            v-for="msg in messages"
            :key="msg.id"
            :class="['msg-bubble', msg.direction === 'outbound' ? 'msg-outbound' : 'msg-inbound']"
          >
            <div class="msg-header">
              <span class="msg-sender">
                <strong>{{ msg.direction === 'outbound' ? 'Astro Way' : (msg.fromName || msg.fromAddress) }}</strong>
                <span v-if="msg.direction === 'inbound'" class="msg-email">&lt;{{ msg.fromAddress }}&gt;</span>
              </span>
              <span class="msg-time">
                <span v-if="msg.direction === 'outbound'" :class="['status-mini', `status-${msg.sendStatus || 'unknown'}`]">
                  {{ formatSendStatus(msg.sendStatus) }}
                </span>
                <span v-else-if="msg.draftStatus" :class="['status-mini', `status-${msg.draftStatus}`]">
                  {{ formatDraftStatus(msg.draftStatus) }}
                </span>
                {{ formatTimestamp(msg.createdAt) }}
              </span>
            </div>
            <pre class="msg-body">{{ messageDisplayBody(msg) }}</pre>
            <div v-if="msg.errorMessage" class="msg-error">Error: {{ msg.errorMessage }}</div>
          </div>
        </div>
      </data-panel>

      <data-panel :title="replySectionTitle">
        <template #actions>
          <button
            v-if="latestPendingInbound && latestPendingInbound.aiDraft"
            class="link-btn"
            @click="useAiDraft"
          >
            Use AI draft →
          </button>
        </template>

        <div class="reply-body">
          <p v-if="!latestPendingInbound" class="follow-up-hint">
            ⚠️ The customer has not sent a new message yet. Anything you write here will be sent as a <strong>follow-up</strong> in the same thread.
          </p>

          <div v-if="latestPendingInbound && latestPendingInbound.aiDraft && !aiDraftUsed" class="ai-draft-preview">
            <div class="draft-head">
              <span class="ai-label">AI Suggestion</span>
              <button class="copy-btn" @click="copyToClipboard(latestPendingInbound.aiDraft, 'draft')">
                <svg v-if="copiedKey !== 'draft'" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                {{ copiedKey === 'draft' ? 'Copied' : 'Copy' }}
              </button>
            </div>
            <pre class="draft-text">{{ latestPendingInbound.aiDraft }}</pre>
          </div>

          <textarea
            v-model="finalReply"
            class="final-textarea"
            rows="10"
            :placeholder="latestPendingInbound ? 'Edit or paste the final reply...' : 'Write a follow-up message...'"
          ></textarea>

          <div class="action-row">
            <button
              v-if="latestPendingInbound"
              @click="discard"
              class="btn btn-secondary"
              :disabled="discarding || sending"
            >
              {{ discarding ? 'Discarding...' : 'Discard' }}
            </button>
            <button
              @click="send"
              :class="['btn', latestPendingInbound ? 'btn-primary' : 'btn-followup']"
              :disabled="!finalReply.trim() || sending || discarding"
            >
              <svg v-if="!sending" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
              {{ sendButtonLabel }}
            </button>
          </div>
        </div>
      </data-panel>
    </template>
  </dashboard-layout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { httpsCallable } from 'firebase/functions'
import { functions } from '../firebase'
import { FirebaseEmailRepliesService } from '../services/firebaseEmailRepliesService'
import DashboardLayout from './ui/DashboardLayout.vue'
import DataPanel from './ui/DataPanel.vue'
import StatusBadge from './ui/StatusBadge.vue'
import LoadingState from './ui/LoadingState.vue'
import EmptyState from './ui/EmptyState.vue'

const sendThreadMessageFn = httpsCallable(functions, 'sendThreadMessage')
const discardInboundDraftFn = httpsCallable(functions, 'discardInboundDraft')

export default {
  name: 'EmailReplyDetailView',
  components: { DashboardLayout, DataPanel, StatusBadge, LoadingState, EmptyState },
  setup() {
    const route = useRoute()
    const service = new FirebaseEmailRepliesService()
    const thread = ref(null)
    const messages = ref([])
    const loading = ref(true)
    const sending = ref(false)
    const discarding = ref(false)
    const finalReply = ref('')
    const aiDraftUsed = ref(false)
    const copiedKey = ref(null)
    let copyResetTimer = null

    const load = async () => {
      try {
        const { thread: t, messages: m } = await service.getThreadWithMessages(route.params.id)
        thread.value = t
        messages.value = m
        finalReply.value = ''
        aiDraftUsed.value = false
      } catch (e) {
        console.error('Error loading thread:', e)
      } finally {
        loading.value = false
      }
    }

    // Latest inbound message that still needs a reply (oldest pending first
    // would be more "FIFO", but the active one in the UI is the most recent
    // pending — usually only one anyway).
    const latestPendingInbound = computed(() => {
      const pending = messages.value
        .filter(m => m.direction === 'inbound' && m.draftStatus === 'pending_review')
        .sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0))
      return pending[0] || null
    })

    const threadState = computed(() => {
      if (!thread.value) return 'unknown'
      if ((thread.value.pendingCount || 0) > 0) return 'pending'
      if ((thread.value.messageCount || 0) > 0) return 'replied'
      return 'unknown'
    })

    const threadStatusLabel = computed(() => {
      const s = threadState.value
      if (s === 'pending') return 'Pending Review'
      if (s === 'replied') return 'Replied'
      return 'Empty'
    })

    const threadStatusVariant = computed(() => {
      const s = threadState.value
      if (s === 'pending') return 'warning'
      if (s === 'replied') return 'success'
      return 'neutral'
    })

    const replySectionTitle = computed(() => {
      return latestPendingInbound.value ? 'Reply to Customer' : 'Send Follow-up'
    })

    const sendButtonLabel = computed(() => {
      if (sending.value) return 'Sending...'
      return latestPendingInbound.value ? 'Send Reply' : 'Send Follow-up'
    })

    const messageDisplayBody = (msg) => {
      if (msg.direction === 'outbound') {
        return msg.finalReply || msg.bodyText || '(empty)'
      }
      return msg.bodyText || '(empty)'
    }

    const useAiDraft = () => {
      if (latestPendingInbound.value?.aiDraft) {
        finalReply.value = latestPendingInbound.value.aiDraft
        aiDraftUsed.value = true
      }
    }

    const send = async () => {
      if (sending.value || !finalReply.value.trim()) return

      const isFollowUp = !latestPendingInbound.value
      const customer = thread.value.customerEmail
      const confirmMsg = isFollowUp
        ? `Send a FOLLOW-UP to ${customer}?\n\nThe customer has not replied yet. This will create a new outbound message in the thread.`
        : `Send this reply to ${customer}?\n\nThis cannot be undone.`
      if (!confirm(confirmMsg)) return

      sending.value = true
      try {
        await sendThreadMessageFn({
          threadId: thread.value.id,
          inReplyToInboundId: isFollowUp ? null : latestPendingInbound.value.id,
          finalReply: finalReply.value
        })
        // Refresh thread state
        await load()
      } catch (e) {
        console.error('Error sending:', e)
        alert('Failed to send: ' + (e.message || 'Unknown error'))
      } finally {
        sending.value = false
      }
    }

    const discard = async () => {
      if (discarding.value || !latestPendingInbound.value) return
      if (!confirm('Discard this draft? No email will be sent for this customer message.')) return
      discarding.value = true
      try {
        await discardInboundDraftFn({
          threadId: thread.value.id,
          inboundMessageId: latestPendingInbound.value.id
        })
        await load()
      } catch (e) {
        console.error('Error discarding:', e)
        alert('Failed to discard: ' + (e.message || 'Unknown error'))
      } finally {
        discarding.value = false
      }
    }

    const copyToClipboard = async (text, key) => {
      if (!text) return
      try {
        await navigator.clipboard.writeText(text)
        copiedKey.value = key
        if (copyResetTimer) clearTimeout(copyResetTimer)
        copyResetTimer = setTimeout(() => { copiedKey.value = null }, 1500)
      } catch (e) {
        console.error('Copy failed:', e)
      }
    }

    const formatTimestamp = (ts) => {
      if (!ts) return '-'
      const ms = ts.seconds ? ts.seconds * 1000 : new Date(ts).getTime()
      if (isNaN(ms)) return '-'
      return new Date(ms).toLocaleString('tr-TR', {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit'
      })
    }

    const formatDraftStatus = (s) => {
      if (s === 'pending_review') return 'Pending'
      if (s === 'replied') return 'Replied'
      if (s === 'discarded') return 'Discarded'
      return s || '-'
    }

    const formatSendStatus = (s) => {
      if (s === 'sent') return 'Sent'
      if (s === 'failed') return 'Failed'
      return s || '-'
    }

    onMounted(load)

    return {
      thread,
      messages,
      loading,
      sending,
      discarding,
      finalReply,
      aiDraftUsed,
      copiedKey,
      latestPendingInbound,
      threadState,
      threadStatusLabel,
      threadStatusVariant,
      replySectionTitle,
      sendButtonLabel,
      messageDisplayBody,
      useAiDraft,
      send,
      discard,
      copyToClipboard,
      formatTimestamp,
      formatDraftStatus,
      formatSendStatus
    }
  }
}
</script>

<style scoped>
/* Thread metadata */
.thread-subject { font-size: 15px; color: var(--text-primary); }
.user-link {
  color: var(--primary-color);
  text-decoration: none;
  font-size: 13px;
}
.user-link:hover { text-decoration: underline; }
.meta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
}
.meta-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.meta-label {
  font-size: 10px;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.meta-value {
  font-size: 13px;
  color: var(--text-primary);
}
.msg-count {
  font-size: 12px;
  color: var(--text-secondary);
}

/* Conversation thread bubbles */
.conversation {
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: rgba(0, 0, 0, 0.18);
  padding: 16px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  max-height: 600px;
  overflow-y: auto;
}
.msg-bubble {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid;
}
.msg-inbound {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.08);
  align-self: flex-start;
  margin-right: 40px;
  max-width: 85%;
  width: 100%;
}
.msg-outbound {
  background: rgba(108, 99, 255, 0.08);
  border-color: rgba(108, 99, 255, 0.2);
  align-self: flex-end;
  margin-left: 40px;
  max-width: 85%;
  width: 100%;
}
.msg-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: var(--text-secondary);
  flex-wrap: wrap;
  gap: 8px;
}
.msg-sender strong { color: var(--text-primary); }
.msg-email { font-family: monospace; opacity: 0.7; }
.msg-time {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: monospace;
  font-size: 11px;
}
.msg-body {
  background: rgba(0, 0, 0, 0.25);
  padding: 10px 14px;
  border-radius: 6px;
  color: var(--text-primary);
  font-size: 13px;
  font-family: inherit;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  border: 1px solid rgba(255, 255, 255, 0.04);
}
.msg-error {
  font-size: 11px;
  color: #fca5a5;
  background: rgba(239, 68, 68, 0.08);
  padding: 6px 10px;
  border-radius: 6px;
}

/* Reply composer */
.reply-body {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.ai-label {
  font-size: 11px;
  color: #a78bfa;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}
.ai-draft-preview {
  background: rgba(108, 99, 255, 0.04);
  border: 1px solid rgba(108, 99, 255, 0.18);
  border-radius: 8px;
  padding: 10px 12px;
}
.draft-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}
.draft-text {
  background: transparent;
  padding: 0;
  margin: 0;
  font-size: 12px;
  color: var(--text-primary);
  font-family: inherit;
  line-height: 1.55;
  white-space: pre-wrap;
  word-break: break-word;
}
.copy-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: var(--text-secondary);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.copy-btn:hover:not(:disabled) {
  background: rgba(108, 99, 255, 0.15);
  color: var(--text-primary);
}
.final-textarea {
  width: 100%;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 14px 18px;
  color: var(--text-primary);
  font-size: 13px;
  font-family: inherit;
  line-height: 1.6;
  resize: vertical;
  box-sizing: border-box;
}
.final-textarea:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(108, 99, 255, 0.2);
}
.action-row {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 18px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.15s ease;
}
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-secondary {
  background: rgba(255, 255, 255, 0.06);
  color: var(--text-primary);
  border: 1px solid rgba(255, 255, 255, 0.1);
}
.btn-secondary:hover:not(:disabled) { background: rgba(255, 255, 255, 0.1); }
.btn-primary {
  background: linear-gradient(135deg, var(--primary-color), var(--gradient-2));
  color: white;
  box-shadow: 0 2px 10px rgba(108, 99, 255, 0.3);
}
.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(108, 99, 255, 0.4);
}
.btn-followup {
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
  box-shadow: 0 2px 10px rgba(245, 158, 11, 0.3);
}
.btn-followup:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(245, 158, 11, 0.4);
}
.link-btn {
  background: none;
  border: none;
  color: var(--primary-color);
  font-size: 12px;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
}
.follow-up-hint {
  margin: 0;
  padding: 10px 14px;
  background: rgba(245, 158, 11, 0.08);
  border: 1px solid rgba(245, 158, 11, 0.25);
  border-radius: 8px;
  color: #fbbf24;
  font-size: 12px;
  line-height: 1.5;
}

/* Per-message mini status chips */
.status-mini {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
}
.status-pending { background: rgba(251, 191, 36, 0.15); color: #fbbf24; }
.status-pending_review { background: rgba(251, 191, 36, 0.15); color: #fbbf24; }
.status-replied { background: rgba(34, 197, 94, 0.15); color: #34d399; }
.status-sent { background: rgba(34, 197, 94, 0.15); color: #34d399; }
.status-failed { background: rgba(239, 68, 68, 0.15); color: #f87171; }
.status-discarded { background: rgba(148, 163, 184, 0.15); color: #94a3b8; }
.status-unknown { background: rgba(255, 255, 255, 0.05); color: var(--text-secondary); }

@media (max-width: 1024px) {
  .msg-inbound, .msg-outbound { margin: 0; max-width: 100%; }
}
</style>
